import type {ReactElement, ReactNode} from 'react';
import {useEffect, useRef, useState} from 'react';
import {Button, ButtonType, ButtonSize} from '@Components/button';
import TooltipTrigger from '@Components/tooltip-trigger';
import {useAiChat} from '@/ai-chat/providers/ai-chat.context';
import {useAiChatSize} from '@AiChat/hooks/useAiChatSize';
import {AiChatSize} from '@AiChat/hooks/useAiChatSize.types';
import {wasAiChatOpenedFromFakeDoor} from '@Libraries/marketing-planner.library';
import {useImageEditMenu} from '@Components/ai-chat-window/hooks/useImageEditMenu';
import {useResetAiActionLoadingStatesOnModalClose} from '@Components/ai-chat-window/hooks/useResetAiActionLoadingStatesOnModalClose';
import {useIsEventsCalendarPromotionChat} from '@Components/ai-chat-window/hooks/useIsEventsCalendarPromotionChat';
import {ChangeStyleDropdown} from '@Components/ai-chat-window/components/change-style-dropdown/change-style-dropdown';
import {UseThisButton} from '@Components/ai-chat-window/components/use-this-button/use-this-button';
import {useAiChatCallbacks} from '@AiChat/callbacks/ai-chat-callbacks.context';
import {UsedAssetKind} from '@AiChat/callbacks/ai-chat-callbacks.types';
import {LoadingStates} from '@Utils/loading.util';
import type {GeneratedImagePartActionsLoading} from '@/ai-chat/providers/ai-chat-provider.types';
import type {AssistantMessageImagePartActionsRowProps} from './assistant-message-image-part-actions-row.types';
import {MORE_OPTIONS_DROPDOWN_MODAL_PANEL_ID} from './assistant-message-image-part-actions-row.types';
import {hasUserSeenAiChatTurnIntoVideoTooltip, markAiChatTurnIntoVideoTooltipAsSeen} from './assistant-message-image-part-actions-row.library';
import {TurnIntoVideoTooltip} from './components/turn-into-video-tooltip/turn-into-video-tooltip';
import {ImageActionsSkeletonRow} from './components/image-actions-skeleton-row/image-actions-skeleton-row';
import {ImageActionsOverflowDropdown} from './components/image-actions-overflow-dropdown/image-actions-overflow-dropdown';
import type {ImageActionAvailabilityContext, ImageActionDescriptor} from './image-actions.types';
import {ImageActionId} from './image-actions.types';
import {resolveImageActions} from './resolve-image-actions';
import {useImageActionHandlers} from './useImageActionHandlers';
import styles from './assistant-message-image-part-actions-row.module.scss';

export function AssistantMessageImagePartActionsRow({...props}: AssistantMessageImagePartActionsRowProps): ReactElement | null {
  const {state} = useAiChat();
  const {size} = useAiChatSize();
  const {onUseAsset} = useAiChatCallbacks();
  const isEventsCalendarPromotionChat = useIsEventsCalendarPromotionChat(props.chatId);
  const {handlers, isGeneratingVideo} = useImageActionHandlers(props);

  const [showVideoTooltip, setShowVideoTooltip] = useState(false);
  const [hasSeenVideoTooltip, setHasSeenVideoTooltip] = useState(true);
  const turnIntoVideoRef = useRef<HTMLDivElement>(null);

  useResetAiActionLoadingStatesOnModalClose(props.chatId, props.hashedId);

  useEffect(() => {
    const checkHasSeenVideoTooltip = async (): Promise<void> => {
      const hasSeen = await hasUserSeenAiChatTurnIntoVideoTooltip();
      setHasSeenVideoTooltip(hasSeen);
    };
    void checkHasSeenVideoTooltip();
  }, []);

  const loadingStates: GeneratedImagePartActionsLoading = state.getLoadingStatesForImageActions(props.chatId, props.hashedId);

  const isDisabled =
    loadingStates.publishingLoadingState === LoadingStates.LOADING ||
    loadingStates.shareLoadingState === LoadingStates.LOADING ||
    loadingStates.saveToMyDesignsLoadingState === LoadingStates.LOADING ||
    loadingStates.makeTextEditableLoadingState === LoadingStates.LOADING;

  const imageEditMenu = useImageEditMenu({
    chatId: props.chatId,
    hashedId: props.hashedId,
    hasLayeredPoster: props.hasLayeredPoster,
    moreOptionsPanelId: MORE_OPTIONS_DROPDOWN_MODAL_PANEL_ID,
    isDisabled,
  });

  const availabilityContext: ImageActionAvailabilityContext = {
    isLastMessage: props.isLastMessage,
    canWrite: props.canWrite,
    hasLayeredPoster: props.hasLayeredPoster,
    isEventsCalendarPromotionChat,
    isCreateAVideoFeatureEnabled: state.isAiChatCreateAVideoFeatureEnabled,
    canUseEditableTextLayers: state.canUseEditableTextLayers,
    isPrimaryPublishingOptionPromoted: wasAiChatOpenedFromFakeDoor(),
    hasImage: props.hashedId !== '',
  };

  const resolved = resolveImageActions({size, context: availabilityContext});

  const showsUseThisPill = !!onUseAsset && !!props.hashedId && !props.isImagePending;

  const getUseThisPill = (): ReactNode => {
    if (!showsUseThisPill) {
      return null;
    }

    return <UseThisButton asset={{kind: UsedAssetKind.IMAGE, chatId: props.chatId, messageId: props.messageId, imageHashedId: props.hashedId}} />;
  };

  const getChangeStylePill = (): ReactNode => {
    return (
      <ChangeStyleDropdown
        key={ImageActionId.CHANGE_STYLE}
        chatId={props.chatId}
        onGenerate={props.onGenerate}
        imageHashedId={props.hashedId}
        canWrite={props.canWrite}
      />
    );
  };

  const getVideoTooltip = (): ReactElement | null => {
    if (!showVideoTooltip || size === AiChatSize.SMALL) {
      return null;
    }

    return (
      <TurnIntoVideoTooltip
        referenceElementRef={turnIntoVideoRef}
        onClose={(): void => {
          setShowVideoTooltip(false);
        }}
      />
    );
  };

  const revealVideoTooltipOnce = (): void => {
    if (hasSeenVideoTooltip) {
      return;
    }

    setShowVideoTooltip(true);
    void markAiChatTurnIntoVideoTooltipAsSeen();
    setHasSeenVideoTooltip(true);
  };

  const getTurnIntoVideoPill = (): ReactNode => {
    const buttonNode = (
      <div
        ref={turnIntoVideoRef}
        onMouseEnter={revealVideoTooltipOnce}
        onMouseLeave={(): void => {
          setShowVideoTooltip(false);
        }}
      >
        <Button
          textClasses={styles.noWrap}
          customClasses={`radius-8 _full-height ${styles.turnIntoVideoButton}`}
          type={ButtonType.SECONDARY}
          size={ButtonSize.SMALL}
          text={window.i18next.t('pmwjs_turn_into_video')}
          icon={'icon-video-ai'}
          onClick={handlers[ImageActionId.TURN_INTO_VIDEO]}
          disabled={!props.canWrite || isGeneratingVideo}
        />
        {getVideoTooltip()}
      </div>
    );

    if (!isGeneratingVideo) {
      return buttonNode;
    }

    return (
      <TooltipTrigger
        key={ImageActionId.TURN_INTO_VIDEO}
        text={window.i18next.t('pmwjs_unavailable_while_generating')}
        overlayClass={styles.turnIntoVideoTooltipWrapper}
        tooltipClass={styles.turnIntoVideoTooltip}
      >
        {buttonNode}
      </TooltipTrigger>
    );
  };

  const getGenericPill = (descriptor: ImageActionDescriptor): ReactNode => {
    if (descriptor.id === ImageActionId.CHANGE_STYLE) {
      return null;
    }

    let type = ButtonType.SECONDARY;

    if (descriptor.id === ImageActionId.PUBLISH_TO_SOCIAL_MEDIA && availabilityContext.isPrimaryPublishingOptionPromoted) {
      type = ButtonType.PRIMARY;
    }

    return (
      <Button
        key={descriptor.id}
        textClasses={styles.noWrap}
        customClasses="radius-8"
        type={type}
        size={ButtonSize.SMALL}
        icon={descriptor.icon}
        text={window.i18next.t(descriptor.labelKey)}
        onClick={handlers[descriptor.id]}
        disabled={!props.canWrite || loadingStates.publishingLoadingState === LoadingStates.LOADING}
      />
    );
  };

  const getInlinePill = (descriptor: ImageActionDescriptor): ReactNode => {
    if (descriptor.id === ImageActionId.CHANGE_STYLE) {
      return getChangeStylePill();
    }

    if (descriptor.id === ImageActionId.TURN_INTO_VIDEO) {
      return getTurnIntoVideoPill();
    }

    return getGenericPill(descriptor);
  };

  const getActions = (): ReactNode => {
    if (props.isImagePending) {
      return <ImageActionsSkeletonRow inlineCount={resolved.inline.length} hasOverflow={resolved.overflow.length > 0} />;
    }

    return (
      <>
        {getUseThisPill()}
        {resolved.inline.map(getInlinePill)}
        {resolved.overflow.length > 0 && (
          <ImageActionsOverflowDropdown
            chatId={props.chatId}
            hashedId={props.hashedId}
            canWrite={props.canWrite}
            isDisabled={isDisabled}
            isGeneratingVideo={isGeneratingVideo}
            overflowActions={resolved.overflow}
            handlers={handlers}
            loadingStates={loadingStates}
            imageEditMenu={imageEditMenu}
          />
        )}
      </>
    );
  };

  if (!props.isLastMessage) {
    if (!showsUseThisPill) {
      return null;
    }

    return <div className={`${styles.suggestedPillsSection} _position-relative`}>{getUseThisPill()}</div>;
  }

  return <div className={`${styles.suggestedPillsSection} _position-relative`}>{getActions()}</div>;
}
